/**
 * Main App Component
 * Routing and authentication setup
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { UploadProvider } from './context/UploadContext';
import { ScaffoldGenerationProvider } from './context/ScaffoldGenerationContext';
import { QuizGenerationProvider } from './context/QuizGenerationContext';
import UploadTracker from './components/shared/UploadTracker';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthPage from './pages/auth/AuthPage';
import MarketingLanding from './pages/landing/LandingPage';

import TeacherDashboard from './pages/teacher/TeacherDashboard';
import CourseDetailPage from './pages/teacher/CourseDetailPage';
import StudentDashboard from './pages/student/StudentDashboard';
import StudentCoursePage from './pages/student/StudentCoursePage';
import LecturePlayerPage from './pages/student/LecturePlayerPage';
import CalendarPage from './pages/shared/CalendarPage';
import RatingPage from './pages/shared/RatingPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import SettingsPage from './pages/settings/SettingsPage';

// Placeholder components
const NotFound = () => <div className="p-8 text-gray-900 dark:text-white">404 - Page Not Found</div>;

// Landing page - redirects based on auth status
const LandingPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    // Redirect to appropriate dashboard
    if (user?.role === 'admin') {
      return <Navigate to="/dashboard/admin" replace />;
    } else if (user?.role === 'teacher') {
      return <Navigate to="/dashboard/teacher" replace />;
    } else {
      return <Navigate to="/dashboard/student" replace />;
    }
  }

  // Not authenticated - show the public marketing landing page
  return <MarketingLanding />;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
    <UploadProvider>
    <ScaffoldGenerationProvider>
    <QuizGenerationProvider>
      <Router>
        <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 transition-colors duration-200">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/reset-password" element={<AuthPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/rate/:token" element={<RatingPage />} />


            {/* Admin routes */}
            <Route
              path="/dashboard/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Teacher routes */}
            <Route
              path="/dashboard/teacher"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/courses/:courseId"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <CourseDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/courses/:courseId/lectures/:lectureId"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <LecturePlayerPage />
                </ProtectedRoute>
              }
            />

            {/* Shared: calendar (any authenticated role) */}
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />

            {/* Student routes */}
            <Route
              path="/dashboard/student"
              element={
                <ProtectedRoute requiredRole="student">
                  <StudentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student/courses/:courseId"
              element={
                <ProtectedRoute requiredRole="student">
                  <StudentCoursePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student/courses/:courseId/lectures/:lectureId"
              element={
                <ProtectedRoute requiredRole="student">
                  <LecturePlayerPage />
                </ProtectedRoute>
              }
            />

            {/* Settings (shared page, separate routes per role) */}
            <Route
              path="/teacher/settings"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student/settings"
              element={
                <ProtectedRoute requiredRole="student">
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        <UploadTracker />
      </Router>
    </QuizGenerationProvider>
    </ScaffoldGenerationProvider>
    </UploadProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
